<template>
  <div class="h-[280px] relative">
    <Bar :data="chartData" :options="chartOptions" />
  </div>
</template>

<script setup lang="ts">
import { computed, shallowRef, defineProps } from 'vue'
import { Bar } from 'vue-chartjs'
import { useAccountStore } from '@/stores/account'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
)

const props = defineProps({
  startDate: {
    type: Date,
    required: true
  },
  endDate: {
    type: Date,
    required: true
  }
})

const accountStore = useAccountStore()

const chartOptions = shallowRef({
  responsive: true,
  maintainAspectRatio: false,
  animation: {
    duration: 500
  },
  plugins: {
    legend: {
      position: 'bottom' as const,
      labels: {
        usePointStyle: true,
        padding: 16,
        boxWidth: 6,
        boxHeight: 6,
        font: {
          size: 11
        }
      }
    },
    title: {
      display: true,
      text: 'Comparativo Mensal',
      font: {
        size: 14,
        weight: 'bold'
      },
      padding: {
        bottom: 15
      }
    }
  },
  scales: {
    x: {
      grid: {
        display: false
      },
      ticks: {
        maxRotation: 0,
        font: {
          size: 11
        }
      }
    },
    y: {
      beginAtZero: true,
      grid: {
        borderDash: [2, 2]
      },
      ticks: {
        font: {
          size: 11
        },
        callback: (value: number) => {
          return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
            notation: 'compact'
          }).format(value)
        }
      }
    }
  }
})

const chartData = computed(() => {
  if (!accountStore.accounts?.length) {
    return {
      labels: [],
      datasets: []
    }
  }

  const startDate = props.startDate
  const endDate = props.endDate

  // Filtra as contas dentro do período
  const filteredAccounts = accountStore.accounts.filter(account => {
    const dueDate = new Date(account.dueDate)
    return dueDate >= startDate && dueDate <= endDate
  })

  // Agrupa as contas por mês
  const monthlyData = filteredAccounts.reduce((acc, account) => {
    const date = new Date(account.dueDate)
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
    
    if (!acc[monthKey]) {
      acc[monthKey] = {
        revenue: 0,
        expenses: 0,
        investments: 0,
        proLabore: 0
      }
    }

    const value = account.value || 0

    if (account.type === 'receivable') {
      acc[monthKey].revenue += value
    } else if (account.type === 'payable') {
      if (account.costCenterName?.toLowerCase().includes('investimento')) {
        acc[monthKey].investments += value
      } else if (account.costTypeName?.toLowerCase().trim() === 'pró-labore') {
        acc[monthKey].proLabore += value
      } else {
        acc[monthKey].expenses += value
      }
    }

    return acc
  }, {})

  // Converte para o formato do gráfico
  const months = Object.keys(monthlyData).sort()
  const datasets = [
    {
      label: 'Receitas',
      data: months.map(monthKey => monthlyData[monthKey].revenue),
      borderColor: '#22C55E',
      backgroundColor: '#22C55E'
    },
    {
      label: 'Despesas',
      data: months.map(monthKey => monthlyData[monthKey].expenses),
      borderColor: '#EF4444',
      backgroundColor: '#EF4444'
    },
    {
      label: 'Investimentos',
      data: months.map(monthKey => monthlyData[monthKey].investments),
      borderColor: '#A855F7',
      backgroundColor: '#A855F7'
    },
    {
      label: 'Pró-Labore',
      data: months.map(monthKey => monthlyData[monthKey].proLabore),
      borderColor: '#F97316',
      backgroundColor: '#F97316'
    }
  ]

  return {
    labels: months.map(monthKey => {
      const [year, monthNum] = monthKey.split('-')
      return `${monthNum}/${year}`
    }),
    datasets
  }
})
</script>
